const NUMERIC_TYPES = new Set(["currency", "number", "percentage"]);
const EXCLUDED_FIELDS = new Set(["pricing", "invoice_amount"]);

const columnText = (column) => `${column?.key || ""} ${column?.label || ""}`
  .replaceAll("_", " ")
  .toLowerCase();

const basisScore = (mode, column) => {
  const key = String(column?.key || "").toLowerCase();
  const text = columnText(column);
  if (mode === "fte") {
    if (key === "prorated_ftes" || text.includes("prorated fte")) return 0;
    if (key === "billable_ftes" || text.includes("billable fte")) return 1;
    return text.includes("fte") ? 2 : 99;
  }
  if (mode === "transactions") {
    if (text.includes("transaction")) return 0;
    return ["encounter", "claim", "visit", "procedure", "volume", "count", "unit"]
      .some((token) => text.includes(token)) ? 1 : 99;
  }
  if (mode === "man_hours") {
    if (text.includes("man hour") || text.includes("manhour")) return 0;
    return ["hour", "hours", "hrs"].some((token) => text.includes(token)) ? 1 : 99;
  }
  if (mode === "percentage") {
    if (text.includes("basis")) return 0;
    if (text.includes("collection")) return 1;
    if (column?.financial || ["currency", "percentage"].includes(column?.type)) return 2;
    return ["revenue", "amount", "charge", "sales"].some((token) => text.includes(token)) ? 3 : 99;
  }
  return 99;
};

export const resolveBillingBasis = (mode, columns = []) => {
  const candidates = (Array.isArray(columns) ? columns : [])
    .filter((column) => column?.visible !== false
      && NUMERIC_TYPES.has(column?.type)
      && !EXCLUDED_FIELDS.has(column?.key))
    .map((column, index) => ({ column, index, score: basisScore(mode, column) }))
    .filter(({ score }) => score < 99)
    .sort((left, right) => left.score - right.score || left.index - right.index);
  return candidates[0]?.column?.key || (mode === "fte" ? "prorated_ftes" : null);
};

export const billingForForm = (form, service, existing, revenueColumns = []) => {
  const same = existing && ["process", "service"].every(
    (key) => String(form[key] || "").trim().toLowerCase() === String(existing[key] || "").trim().toLowerCase()
  );
  if (same && existing.billing_snapshot) return existing.billing_snapshot;
  if (!service) return { mode: "fte", rate: form.pricing, basis_field: "prorated_ftes" };
  const mode = service.billing_mode || "fte";
  const legacyPercentage = mode === "percentage"
    && !service.billing_basis_field
    && service.collection_field
    && service.percentage_field;
  return {
    mode,
    rate: legacyPercentage
      ? service.custom?.[service.percentage_field]
      : mode === "percentage"
        ? (service.percentage_rate ?? service.pricing)
        : (form.pricing === "" || form.pricing == null ? service.pricing : form.pricing),
    basis_field: legacyPercentage
      ? service.collection_field
      : (service.billing_basis_field || resolveBillingBasis(mode, revenueColumns)),
    ...(legacyPercentage
      ? {
          percentage_field: service.percentage_field,
          collection_field: service.collection_field,
        }
      : {}),
  };
};

export const invoicePreview = (detail) => {
  const basis = detail.billing_snapshot || { mode: "fte" };
  const mode = basis.mode || "fte";
  const basisField = basis.basis_field || basis.collection_field || "prorated_ftes";
  const value = detail[basisField] ?? detail.custom?.[basisField];
  if (mode !== "fte" && (value == null || value === "" || basis.rate == null)) return null;
  const rate = Number(basis.rate ?? detail.pricing ?? 0);
  const quantity = Number(value ?? 0);
  const amount = mode === "percentage"
    ? quantity * rate / 100
    : rate * quantity;
  return Number.isFinite(amount) ? Math.round(amount * 100) / 100 : null;
};